/**
 * URL e ordem das fotos da vistoria (at-63, frente D/D2).
 *
 * Existe porque `normalizeImageUrl` estava **copiado em dois lugares**
 * (`views/laudo/LaudoHtml.tsx` e `views/laudo/LaudoVistoria.tsx`, com o bucket
 * escrito à mão nos dois) e a ordenação "avaria primeiro" em outros dois. É a
 * convenção da at-24: regra repetida em duas telas é divergência com data
 * marcada — quem corrigir uma forma de URL corrigiria só metade do app.
 *
 * ⚠️ Funções **puras**, sem React e sem domínio de tela: quem as consome é a
 * miniatura, o documento do laudo e as telas de leitura.
 */

/**
 * Bucket público do GCS onde as fotos de vistoria são gravadas.
 *
 * ⚠️ Estava **hardcoded no meio da função**, nas duas cópias. Continua sendo o
 * mesmo valor de antes — o que muda é ter **um** lugar para trocá-lo. Ele é
 * público por construção: o cron que envia as fotos ao SGA as baixa por esta URL
 * (`08-security.md` — é o motivo de `uploadBuffer` continuar público).
 */
export const BUCKET_FOTOS_VISTORIA = 'saac_documentos';

/**
 * Devolve uma URL que o `<img>` consegue abrir, ou `undefined`.
 *
 * As cinco formas que circulam no app, e por que cada uma existe:
 *
 *   1. `blob.http://…` / `blob.https://…` — blob URL que passou por uma
 *      serialização que **comeu os dois-pontos** do esquema. Vem do Redux
 *      persistido (foto capturada e ainda não recarregada do banco);
 *   2. `//host/caminho` — URL sem protocolo;
 *   3. `storage.googleapis.com/…` — URL do GCS sem protocolo;
 *   4. `vistorias/<id>/photos/…` — **caminho relativo** gravado pelo backend,
 *      que precisa do bucket na frente;
 *   5. qualquer outra coisa — já é URL absoluta e passa intacta.
 *
 * ⚠️ Preservado **verbatim** das duas cópias, na mesma ordem de testes: a ordem
 * importa (o caso 4 é prefixo de nada, mas o 3 casaria com o 5 se viesse depois).
 */
export function normalizarUrlImagem(entrada: unknown): string | undefined {
  if (typeof entrada !== 'string') return undefined;

  const url = entrada.trim();
  if (!url) return undefined;

  if (url.startsWith('blob.http://')) return `blob:http://${url.slice('blob.http://'.length)}`;
  if (url.startsWith('blob.https://')) return `blob:https://${url.slice('blob.https://'.length)}`;
  if (url.startsWith('//')) return `https:${url}`;
  if (url.startsWith('storage.googleapis.com/')) return `https://${url}`;
  if (url.startsWith('vistorias/')) {
    return `https://storage.googleapis.com/${BUCKET_FOTOS_VISTORIA}/${url}`;
  }

  return url;
}

/**
 * As fotos com avaria vêm primeiro — é o que o laudo por veículo já fazia.
 *
 * A avaria é a informação que decide o desfecho da vistoria; enterrá-la na
 * posição 23 de 29 obriga quem lê a percorrer tudo para descobrir se há alguma.
 *
 * ⚠️ **Não muta o array recebido** (o `sort` nativo ordena no lugar, e a entrada
 * costuma vir do Redux, onde mutar é proibido). A ordem relativa dentro de cada
 * grupo é preservada — `Array.prototype.sort` é estável desde o ES2019.
 */
export function ordenarAvariasPrimeiro<T extends { damaged?: boolean }>(fotos: readonly T[]): T[] {
  return [...fotos].sort(
    (a, b) => Number(b.damaged === true) - Number(a.damaged === true),
  );
}
